import React, { useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useUser } from '@/queries/useUser';
import { formatCurrency } from '@/lib/currency';
import { StanButton } from '@/components';
import IcX from '@/assets/icons/ic-x-close.svg';
import { createIconElement } from '@/lib/utils/createIconElement';

interface CashOutCelebrationModalProps {
    isOpen: boolean;
    amount: number;
    currency: string;
    onClose: () => void;
}

export const CashOutCelebrationModal: React.FC<CashOutCelebrationModalProps> = ({
    isOpen,
    amount,
    currency,
    onClose,
}) => {
    const [showConfetti, setShowConfetti] = useState(false);
    const { width, height } = useWindowSize();
    const { user } = useUser();

    useEffect(() => {
        if (isOpen) {
            const startTimer = setTimeout(() => {
                setShowConfetti(true);
            }, 600);

            const stopTimer = setTimeout(() => {
                setShowConfetti(false);
            }, 15000);

            return () => {
                clearTimeout(startTimer);
                clearTimeout(stopTimer);
            };
        } else {
            setShowConfetti(false);
        }
        return;
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className='bg-canvas z-backdrop fixed inset-0 size-full'>
            {showConfetti && <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />}

            <div className='flex h-full flex-wrap justify-center'>
                <div className='hidden w-75 flex-grow xl:block' />

                <div className='p-5'>
                    <div className='flex h-full max-w-full min-w-80 flex-col flex-wrap justify-between'>
                        <div className='mx-auto w-25 pt-5'>
                            <img src='src/assets/images/stan-logo-text.png' alt='Stan Logo' />
                        </div>

                        <div className='flex-grow' />

                        <div className='mx-auto text-center'>
                            <p className='text-dark mb-4'>
                                <span className='mr-2'>🎉</span>
                                <span className='hidden md:inline'>Congratulations! </span>
                                <span>Your Funds are on the Way.</span>
                            </p>
                            <p className='text-display-display1 truncate'>{formatCurrency(amount, currency)}!</p>
                        </div>

                        <div className='flex-grow' />

                        <div className='w-full pb-5'>
                            <StanButton name='cashout-celebration-cancel-button' className='w-full' onClick={onClose}>
                                Woohoo!
                            </StanButton>
                        </div>

                        <button
                            className='bg-neutral-subtle hover:bg-neutral-soft absolute top-5 right-5 z-9 flex size-7 cursor-pointer items-center justify-center rounded-full transition-colors'
                            onClick={onClose}
                            aria-label='Close'
                        >
                            {createIconElement(IcX, { className: 'size-5 text-gray' })}
                        </button>
                    </div>
                </div>

                <div className='hidden flex-grow xl:block'>
                    <div className='flex h-full w-75 flex-col justify-between'>
                        <div />
                        <div className='ml-5 flex justify-center'>
                            <div className='space-y-3'>
                                <div className='bg-neutral-subtle fade-slide-in inline-block rounded-lg px-3 py-2 text-sm ![animation-delay:4s]'>
                                    Did you know that only 5% of Creators on the Internet make any money online?
                                </div>
                                <div className='bg-neutral-subtle fade-slide-in inline-block rounded-lg px-3 py-2 text-sm ![animation-delay:5.5s]'>
                                    And you&apos;re in that 5%!
                                </div>
                                <div className='bg-neutral-subtle fade-slide-in block rounded-lg px-3 py-2 text-sm ![animation-delay:7s]'>
                                    We&apos;re so proud of you @{user?.username}! Keep rockin&apos;!!
                                </div>
                            </div>
                        </div>
                        <div />
                    </div>
                </div>
            </div>
        </div>
    );
};
